import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { apiService } from '../../api/api.service';
import type { IStreamRecording } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import Loader from '../shared/Loader';
import RecordingsVideoPreviewDialog from './RecordingsVideoPreviewDialog';
import CustomVideoPlayer from './CustomVideoPlayer';
import { PlayArrowIcon, DeleteIcon } from '../icons';
import styles from './Recordings.module.scss';
import ConfirmDialog from '../shared/ConfirmDialog';
import { useNotification } from '../shared/NotificationSystem';
import PublishRecordingDialog, { type PublishRecordingFormData } from './PublishRecordingDialog';
import { createPublishAtFromDateTime } from '../../utils/dateUtils';
import DownloadLinkDialog from './DownloadLinkDialog';

const Recordings: React.FC = () => {
	const { t } = useTranslation();
	const { admin } = useAuth();
	const { showSuccess, showError } = useNotification();
	const [loading, setLoading] = useState(true);
	const [recordings, setRecordings] = useState<IStreamRecording[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const [previewUrl, setPreviewUrl] = useState('');
	const [previewTitle, setPreviewTitle] = useState('');
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [recordingToDelete, setRecordingToDelete] = useState<IStreamRecording | null>(null);
	const [publishingRecordings, setPublishingRecordings] = useState<Set<number>>(new Set());
	const [publishDialogOpen, setPublishDialogOpen] = useState(false);
	const [recordingToPublish, setRecordingToPublish] = useState<IStreamRecording | null>(null);
	const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
	const [downloadUrl, setDownloadUrl] = useState('');

	useEffect(() => {
		let isMounted = true;
		const load = async () => {
			try {
				const data = await apiService.getStreamRecordings();
				if (isMounted) setRecordings(data);
			} catch (e) {
				if (isMounted) setError(t('errors.genericApiError'));
			} finally {
				if (isMounted) setLoading(false);
			}
		};
		load();
		return () => {
			isMounted = false;
		};
	}, [t]);

	const openPreview = (url: string, title: string, recordingId: number) => {
		setPreviewUrl(url);
		setPreviewTitle(title);
		setIsPreviewOpen(true);
	};

	const handleDownload = (url: string) => {
		setDownloadUrl(url);
		setDownloadDialogOpen(true);
	};

	const handleAskDelete = (rec: IStreamRecording) => {
		setRecordingToDelete(rec);
		setDeleteDialogOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (!recordingToDelete) return;
		try {
			await apiService.deleteStreamRecording(recordingToDelete.id);
			setRecordings(prev => prev.filter(r => r.id !== recordingToDelete.id));
			showSuccess(t('notifications.success.deleted') || 'Удалено');
		} catch (e) {
			showError(t('notifications.errors.deleteFailed') || 'Не удалось удалить запись');
		} finally {
			setDeleteDialogOpen(false);
			setRecordingToDelete(null);
		}
	};

	const handleAskPublish = (rec: IStreamRecording) => {
		if (rec.is_published_as_training || publishingRecordings.has(rec.id)) return;
		setRecordingToPublish(rec);
		setPublishDialogOpen(true);
	};

	const handleConfirmPublish = async (data: PublishRecordingFormData) => {
		if (!recordingToPublish) return;

		setPublishingRecordings(prev => new Set(prev).add(recordingToPublish.id));
		try {
			const publishAt = createPublishAtFromDateTime(data.publishDate, data.publishTime);

			// Сначала загружаем файл превью, если он есть
			let previewFilePath = undefined;
			if (data.thumbnailFile) {
				const previewUploadResponse = await apiService.uploadPreviewFile(data.thumbnailFile);
				previewFilePath = previewUploadResponse.file_path;
			}

			const res = await apiService.publishStreamRecording(recordingToPublish.id)

			if (res.status === 'success') {
				const body = {
					title: data.title,
					description: data.description,
					publish_at: publishAt,
					preview_file_path: previewFilePath
				}

				await apiService.editVideo('training', res.training_video.id, body);
			}

			setRecordings(prev => prev.map(r =>
				r.id === recordingToPublish.id ? { ...r, is_published_as_training: true } : r
			));
			showSuccess('Запись успешно опубликована');
		} catch (e) {
			showError('Не удалось опубликовать запись');
		} finally {
			setPublishingRecordings(prev => {
				const newSet = new Set(prev);
				newSet.delete(recordingToPublish.id);
				return newSet;
			});
			setPublishDialogOpen(false);
			setRecordingToPublish(null);
		}
	};

	const sortedRecordings = useMemo(() => {
		return [...recordings].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
	}, [recordings]);

	if (admin?.language !== 'RU') {
		return <Navigate to="/content" replace />;
	}

	if (loading) return <Loader />;
	if (error) return <div>{error}</div>;

	if (!sortedRecordings.length) {
		return <div className={styles.emptyState}>Записей пока нет</div>;
	}

	return (
		<div className={styles.container}>
			<header className={styles.header}>
				<h1 className={styles.headerTitle}>{t('navigation.recordings')}</h1>
			</header>

			<section className={styles.section}>
				<div className={styles.grid}>
					{sortedRecordings.map((rec) => (
						<div key={rec.id} className={styles.card}>
							<div className={styles.cardActions}>
								<div className={styles.statusBadge}>
									{rec.is_published_as_training ? 'Опубликовано' : 'Не опубликовано'}
								</div>
								<button className={styles.iconButton} title={t('dailyTrainings.deleteTooltip')} onClick={() => handleAskDelete(rec)}>
									<DeleteIcon />
								</button>
							</div>
							<div className={styles.cardMedia} onClick={() => openPreview(rec.url, rec.title || `Запись #${rec.id}`, rec.id)}>
								<CustomVideoPlayer
									src={rec.url}
									preload="metadata"
									playsInline
									showControls={false}
									className={`${styles.cardMediaVideo} ${styles.previewMode}`}
								/>
								<div className={styles.playIcon}>
									<PlayArrowIcon />
								</div>
							</div>
							<div className={styles.cardContent}>
								<h3 className={styles.cardTitle}>{rec.title || `Запись #${rec.id}`}</h3>
								<div className={styles.cardMeta}>Создано: {new Date(rec.created_at).toLocaleString()}</div>
								<div className={styles.actions}>
									{!rec.is_published_as_training && (
										<button
											className={styles.publishButton}
											onClick={() => handleAskPublish(rec)}
											disabled={publishingRecordings.has(rec.id)}
										>
											{publishingRecordings.has(rec.id) ? 'Публикуется...' : 'Опубликовать'}
										</button>
									)}
									<button className={styles.downloadLink} onClick={() => handleDownload(rec.url)}>
										Скачать
									</button>
								</div>
							</div>
						</div>
					))}
				</div>
			</section>

			<RecordingsVideoPreviewDialog
				open={isPreviewOpen}
				onClose={() => setIsPreviewOpen(false)}
				title={previewTitle}
				videoUrl={previewUrl}
			/>

			<ConfirmDialog
				open={deleteDialogOpen}
				title={t('dailyTrainings.deleteDialog.title')}
				message={t('dailyTrainings.deleteDialog.message', { title: recordingToDelete?.title || `Запись #${recordingToDelete?.id}` })}
				onConfirm={handleConfirmDelete}
				onCancel={() => { setDeleteDialogOpen(false); setRecordingToDelete(null); }}
			/>

			<PublishRecordingDialog
				open={publishDialogOpen}
				onClose={() => { setPublishDialogOpen(false); setRecordingToPublish(null); }}
				onSubmit={handleConfirmPublish}
				recording={recordingToPublish}
			/>

			<DownloadLinkDialog
				open={downloadDialogOpen}
				downloadUrl={downloadUrl}
				onClose={() => { setDownloadDialogOpen(false); setDownloadUrl(''); }}
			/>
		</div>
	);
};

export default Recordings; 